import React, { useEffect, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import "pdfjs-dist/build/pdf.worker.mjs";
import { BASE_URL } from "../Utils/URLs";
import { isLocalUploadPath, resolveMediaUrl } from "../Utils/mediaUrl";

/**
 * Renders the first page of a PDF as thumbnail image. Use instead of generic pdf icon
 * so document cards show actual preview. Falls back to fallbackSrc on error or while loading.
 */
async function renderPdfFirstPageToDataUrl(pdfUrl) {
  const pdf = await pdfjsLib.getDocument({ url: pdfUrl, disableRange: false }).promise;
  const page = await pdf.getPage(1);
  const viewport = page.getViewport({ scale: 1.2 });
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  canvas.width = Math.floor(viewport.width);
  canvas.height = Math.floor(viewport.height);
  await page.render({ canvasContext: ctx, viewport }).promise;
  return canvas.toDataURL("image/png");
}

export default function PdfThumbnail({ pdfUrl, pdfRequest, fallbackSrc = "/driver_forms/assets/images/pdf.png", ...imgProps }) {
  const [src, setSrc] = useState(fallbackSrc);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let blobUrlToRevoke = null;
    let cancelled = false;

    const load = async () => {
      try {
        const getApiOrigin = () => {
          try {
            return new URL(String(BASE_URL || "")).origin;
          } catch (_) {
            return "";
          }
        };
        const getApiBase = () => {
          const raw = String(BASE_URL || "").replace(/\/+$/, "");
          if (!raw) return "";
          return raw; // already contains "/api" or "/api_new/api"
        };
        const isLikelyCrossOriginStorage = (u) => {
          const s = String(u || "");
          return (
            s.includes("storage.googleapis.com/") ||
            s.includes(".storage.googleapis.com/") ||
            s.includes(".s3.") ||
            s.includes("s3.amazonaws.com/")
          );
        };
        const proxyUrlFor = (u) => {
          const apiBase = getApiBase();
          if (apiBase) return `${apiBase}/file/proxy?url=${encodeURIComponent(String(u || ""))}`;
          const origin = getApiOrigin();
          if (!origin) return "";
          return `${origin}/api/file/proxy?url=${encodeURIComponent(String(u || ""))}`;
        };

        let urlToUse = isLocalUploadPath(pdfUrl) ? resolveMediaUrl(pdfUrl) : pdfUrl;
        if (pdfRequest && pdfRequest.url) {
          const res = await fetch(pdfRequest.url, pdfRequest.options);
          if (!res.ok || cancelled) return;
          const blob = await res.blob();
          if (cancelled) return;
          blobUrlToRevoke = URL.createObjectURL(blob);
          urlToUse = blobUrlToRevoke;
        } else if (isLikelyCrossOriginStorage(urlToUse)) {
          // Avoid browser CORS errors by fetching via API proxy first
          const proxyUrl = proxyUrlFor(urlToUse);
          if (proxyUrl) {
            const res = await fetch(proxyUrl, { method: "GET" });
            if (res.ok && !cancelled) {
              const blob = await res.blob();
              if (cancelled) return;
              blobUrlToRevoke = URL.createObjectURL(blob);
              urlToUse = blobUrlToRevoke;
            }
          }
        }
        if (!urlToUse || cancelled) return;
        const dataUrl = await renderPdfFirstPageToDataUrl(urlToUse);
        if (!cancelled) {
          setSrc(dataUrl);
          setLoaded(true);
        }
      } catch (_) {
        if (!cancelled) setSrc(fallbackSrc);
      } finally {
        if (blobUrlToRevoke) URL.revokeObjectURL(blobUrlToRevoke);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [pdfUrl, pdfRequest?.url, fallbackSrc]);

  return <img src={src} alt="" {...imgProps} />;
}
